import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Search, Menu, Command, LogOut, Settings, UserCircle, AlertTriangle, Info, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppSidebar } from "./app-sidebar";
import { useAuth } from "@/stores/auth";
import { authApi, notificationsApi, usersApi, ApiError, type Notification } from "@/lib/api";
import { cn, timeAgo } from "@/lib/utils";

const KIND_LABEL: Record<string, string> = {
  staff: "Staff account",
  corporate: "Corporate account",
  personal: "Personal account",
  client: "Client account",
};

function UserMenu() {
  const navigate = useNavigate();
  const session = useAuth((s) => s.session);
  const clear = useAuth((s) => s.clear);

  const handleLogout = async () => {
    try {
      if (session?.refreshToken) await authApi.logout(session.refreshToken);
    } catch {
      // best-effort — clear local session regardless of server response
    } finally {
      clear();
      toast.success("Signed out");
      navigate("/auth/login", { replace: true });
    }
  };

  const [fullName, setFullName] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    usersApi
      .get(session.userId, session.accessToken)
      .then((p) => {
        if (!cancelled) {
          setFullName(p.full_name || null);
          setPhotoUrl(p.photo_url ?? null);
        }
      })
      .catch(() => {
        // best-effort — falls back to userId-derived initials below
      });
    return () => {
      cancelled = true;
    };
  }, [session?.userId]);

  const initials = fullName
    ? fullName
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
    : session?.userId
      ? session.userId.slice(0, 2).toUpperCase()
      : "U";
  const kindLabel = session ? (KIND_LABEL[session.kind] ?? session.kind) : "Account";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Account menu">
          <Avatar className="h-8 w-8">
            {photoUrl ? <AvatarImage src={photoUrl} alt={fullName ?? "Account"} /> : null}
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground">{fullName ?? kindLabel}</span>
            <span className="truncate text-xs text-muted-foreground">{kindLabel}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/settings">
            <UserCircle className="mr-2 h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/app/settings">
            <Settings className="mr-2 h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotificationBell() {
  const token = useAuth((s) => s.session?.accessToken);
  const [feed, setFeed] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    notificationsApi
      .list(undefined, token)
      .then(setFeed)
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Could not load notifications"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const unreadCount = feed.filter((n) => !n.read_at).length;
  const latest = feed.slice(0, 10);

  const markRead = async (id: string) => {
    const prev = feed;
    const now = new Date().toISOString();
    setFeed((f) => f.map((n) => (n.id === id ? { ...n, read_at: now } : n)));
    try {
      await notificationsApi.markRead(id, token);
    } catch (err) {
      setFeed(prev);
      toast.error(err instanceof ApiError ? err.message : "Could not mark notification as read");
    }
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) load();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 ? (
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-destructive ring-2 ring-surface" />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <DropdownMenuLabel className="p-0 font-semibold">Notifications</DropdownMenuLabel>
          {unreadCount > 0 ? <span className="text-xs text-muted-foreground">{unreadCount} unread</span> : null}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Loading…</div>
          ) : null}
          {!loading && latest.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No notifications</div>
          ) : null}
          {!loading &&
            latest.map((n) => {
              const read = n.read_at !== null;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => !read && markRead(n.id)}
                  className={cn(
                    "flex w-full items-start gap-2.5 border-b px-3 py-2.5 text-left transition-colors last:border-b-0 hover:bg-muted/30",
                    !read && "bg-info/5"
                  )}
                >
                  <div
                    className={cn(
                      "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                      n.severity === "critical" && "bg-destructive/10 text-destructive",
                      n.severity === "warning" && "bg-warning/15 text-warning-foreground",
                      n.severity === "info" && "bg-info/10 text-info"
                    )}
                  >
                    {n.severity === "critical" ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : n.severity === "warning" ? (
                      <Bell className="h-3.5 w-3.5" />
                    ) : (
                      <Info className="h-3.5 w-3.5" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{n.title}</span>
                      {!read ? <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-info" /> : null}
                    </div>
                    {n.detail ? <p className="truncate text-xs text-muted-foreground">{n.detail}</p> : null}
                    <span className="mt-0.5 block text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                  </div>
                </button>
              );
            })}
        </div>
        <DropdownMenuSeparator className="m-0" />
        <Link
          to="/app/notifications"
          className="flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium text-primary hover:bg-muted/30"
        >
          <CheckCheck className="h-3.5 w-3.5" /> Show more
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface TopbarProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export function Topbar({ title, subtitle, actions }: TopbarProps) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-surface/70 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-surface/60 md:px-6 shadow-[0_1px_0_0_var(--blue-100)]">
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-72 border-r-0 bg-sidebar p-0 text-sidebar-foreground">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <div className="[&>aside]:flex [&>aside]:w-full">
            <AppSidebar />
          </div>
        </SheetContent>
      </Sheet>

      <div className="min-w-0 flex-1">
        <h1 className="truncate text-lg font-semibold tracking-tight">{title}</h1>
        {subtitle ? <p className="truncate text-xs text-muted-foreground">{subtitle}</p> : null}
      </div>

      <div className="relative hidden md:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search parcels, drivers, addresses…"
          className="h-9 w-72 pl-9 pr-14 bg-surface-muted"
          aria-label="Search"
        />
        <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 rounded border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground md:inline-flex">
          <Command className="h-3 w-3" />K
        </kbd>
      </div>

      <div className="flex items-center gap-2">
        {actions}
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}
